import React, { useContext, useEffect, useState } from "react";
import Switch from "@material-ui/core/Switch";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputLabel from "@material-ui/core/InputLabel";
import {
  Checkbox, FormControl, MenuItem, Select,
} from "@material-ui/core";
import MainContext from "../../context/main/mainContext";
import dates from "../../utilities/dates";
import { leadCaptureTierOptions } from "../../checkout/config";
import ConfirmationModal from "../../components/ConfrimationModal";
import { PricingPreview } from "../../components/PricingPreview";

const LeadCaptureUpgradeSection = ({
  customerPaymentMethods,
  internalDemoTeam,
  team,
  stripeCustomer,
  setOpenStripePaymentModal,
  setIsLoading,
  changePlan,
  isLoading,
  isPriceMonthly,
  stripePromise,
  hasActiveSubscription,
  selectedPlanInterval,
}) => {
  const mainContext = useContext(MainContext);
  const {
    setTeamPricing,
    getCustomerPaymentMethods,
    previewStripeSubscriptionNewBilling,
    resetPreviewState,
  } = mainContext;
  const [leadsNumber, setLeadsNumber] = useState(
    internalDemoTeam?.leadsNumber || team?.leadsPaid || 500,
  );
  const [leadsNumberInput, setLeadsNumberInput] = useState(0);
  const [isCustomPricing, setIsCustomPricing] = useState(
    !!internalDemoTeam?.pricePerSeatDollars || !!internalDemoTeam?.bucketPercentOff,
  );

  const [customBucketDiscount, setCustomBucketDiscount] = useState(
    internalDemoTeam?.bucketPercentOff || 0,
  );
  const [customBucketDiscountChecked, setCustomBucketDiscountChecked] = useState(
    !!internalDemoTeam?.bucketPercentOff,
  );
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [chargeAutomatically, setChargeAutomatically] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const defaultEndDate = new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 15);
  const hasPaymentMethod = customerPaymentMethods?.length > 0;
  const needsPaymentMethod = chargeAutomatically && !hasPaymentMethod;
  const dueDate = dates.formatTrialEnd(defaultEndDate.toISOString());
  const daysUntilDue = dates.getDaysBetween(new Date(), dueDate);
  const shouldProrateLeads = !isPriceMonthly && hasActiveSubscription && team?.team?.enterprisePlan === "60";
  const defaultYearlyForMonthlyUpgrades = !shouldProrateLeads
    ? "year"
    : selectedPlanInterval?.interval;
  const isElcUpgrade = true;

  useEffect(() => {
    if (stripeCustomer?.id) {
      getCustomerPaymentMethods(
        {
          stripeCustomerId: stripeCustomer.id,
        },
        (err) => {
          if (err) {
            alert(err);
          }
        },
      );
    }
  }, [stripeCustomer]);

  const doPreview = async () => {
    setIsLoadingPreview(true);
    await previewStripeSubscriptionNewBilling(
      {
        isElcUpgrade: isElcUpgrade && shouldProrateLeads,
        teamId: team.team.id,
        number_seats: internalDemoTeam?.numberSeats ?? 5,
        price_per_seat_yearly_dollars: null,
        discount_factor:
          isCustomPricing && customBucketDiscountChecked ? 1 - customBucketDiscount / 100 : 1,
        leadsNumber: leadsNumber === 50001 ? leadsNumberInput : leadsNumber,
        interval: defaultYearlyForMonthlyUpgrades,
        intervalCount: selectedPlanInterval?.intervalCount ?? 1,
      },
      (err) => {
        if (err) {
          alert(err);
        }
        setIsLoadingPreview(false);
      },
    );
  };

  const handleUpgrade = async () => {
    setIsLoading(true);

    const payload = {
      teamId: team.team.id,
      stripeCustomerId: team.team.stripeCustomer,
      companyName: team.team.companyName || "",
      isUpgrade: true,
      // Plan Info
      numberSeats: internalDemoTeam?.numberSeats ?? 5,
      leadsNumber: leadsNumber === 50001 ? leadsNumberInput : leadsNumber,
      teamOwnerName: stripeCustomer?.name || internalDemoTeam?.teamOwnerName || "",

      // Pricing details
      stripeAddress: {
        country: stripeCustomer?.address?.country || "US",
        zipcode: stripeCustomer?.address?.postal_code || "",
        state: stripeCustomer?.address?.state || "",
        addressLine: stripeCustomer?.address?.line1 || "",
        city: stripeCustomer?.address?.city || "",
      },
      chargeAutomatically,
      shouldProrateLeads,
      customPricing: isCustomPricing && customBucketDiscountChecked,
      pricePerSeatDollars: internalDemoTeam?.pricePerSeatDollars || null,
      customBucketDiscountFactor:
        isCustomPricing && customBucketDiscountChecked ? 1 - customBucketDiscount / 100 : 1,
      customMemo: internalDemoTeam?.quotedCustomMemo || "",
      isElcUpgrade,
      daysUntilDue,
      discountFactor:
        isCustomPricing && customBucketDiscountChecked ? 1 - customBucketDiscount / 100 : 1,
      interval: defaultYearlyForMonthlyUpgrades,
      intervalCount: selectedPlanInterval?.intervalCount ?? 1,
    };

    await setTeamPricing(payload, (err) => {
      if (!err) {
        changePlan(team?.team?.id);
      }
      setIsLoading(false);
    });
  };

  return (
    <div>
      <p>This will upgrade the subscription to annual and add ELC features</p>
      <div style={{ marginBottom: "1rem" }}>
        <FormControl style={{ minWidth: 200, marginRight: "1rem" }}>
          <InputLabel id="leads-bucket-label">Leads Bucket*</InputLabel>
          <Select
            labelId="leads-bucket-label"
            value={leadsNumber}
            onChange={(e) => {
              if (e.target.value === 50001) {
                setLeadsNumberInput(50001);
              }
              setLeadsNumber(e.target.value);
            }}
            disabled={isLoading}
          >
            {leadCaptureTierOptions.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {leadsNumber === 50001 && (
          <TextField
            required
            variant="outlined"
            value={leadsNumberInput}
            type="number"
            label="Number of Leads"
            onChange={(e) => setLeadsNumberInput(parseInt(e.target.value, 10))}
            onBlur={(e) => {
              const value = parseInt(e.target.value, 10);
              if (value < 50001) {
                const option = leadCaptureTierOptions.findLast((item) => item.value <= value);
                if (option) {
                  setLeadsNumber(option.value);
                }
              }
            }}
            disabled={isLoading}
            style={{ marginTop: "0.5rem" }}
          />
        )}
      </div>
      <FormGroup row style={{ alignItems: "center", marginBottom: "0.5rem" }}>
        <FormControlLabel
          control={
            <Switch
              checked={isCustomPricing}
              onChange={(e) => setIsCustomPricing(e.target.checked)}
              name="customPricing"
              inputProps={{ "aria-label": "custom pricing checkbox" }}
              disabled={isLoading}
            />
          }
          label="Custom Pricing"
        />
      </FormGroup>
      {isCustomPricing && (
        <div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={customBucketDiscountChecked}
                  onChange={(e) => setCustomBucketDiscountChecked(e.target.checked)}
                  disabled={isLoading}
                />
              }
              label=""
            />
            <TextField
              variant="outlined"
              required
              value={customBucketDiscount}
              type="number"
              label="Bucket Discount %"
              onChange={(e) => setCustomBucketDiscount(parseFloat(e.target.value, 10))}
              disabled={isLoading || !customBucketDiscountChecked}
              style={{ marginBottom: "1rem" }}
            />
          </div>
        </div>
      )}
      <FormGroup row style={{ alignItems: "center", marginBottom: "0.5rem" }}>
        <FormControlLabel
          control={
            <Switch
              checked={chargeAutomatically}
              onChange={(e) => setChargeAutomatically(e.target.checked)}
              name="chargeAutomatically"
              inputProps={{ "aria-label": "charge automatically checkbox" }}
              disabled={isLoading}
            />
          }
          label="Charge Automatically"
        />
      </FormGroup>
      {needsPaymentMethod && (
        <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
          <div style={{ color: "#d32f2f", fontSize: "14px", marginBottom: "0.5rem" }}>
            A payment method is required when charging automatically.
          </div>
          <button
            className="buttonContainerAlt"
            onClick={() => setOpenStripePaymentModal(true)}
            style={{
              backgroundColor: "black",
              color: "white",
            }}
            disabled={!stripePromise || !team?.team?.stripeCustomer}
          >
            Add Payment Method
          </button>
        </div>
      )}
      {shouldProrateLeads && (
        <p
          style={{
            fontSize: "13px",
            color: "#757575",
            fontStyle: "italic",
            marginTop: "0.5rem",
          }}
        >
          Note: Will be credited for the current month, so newly created subs are still given full month proration
        </p>
      )}
      {!shouldProrateLeads && (
        <p
          style={{
            fontSize: "13px",
            color: "#757575",
            fontStyle: "italic",
            marginTop: "0.5rem",
          }}
        >
          Note: Subscription will be upgraded to a 1 year subscription for either monthly DBC or
          free trial
        </p>
      )}
      <div style={{ display: "flex", marginTop: "1rem", gap: "1rem" }}>
        <button
          className="buttonContainerAlt"
          onClick={() => {
            if (isLoading || (chargeAutomatically && !hasPaymentMethod)) return;
            switch (!shouldProrateLeads) {
            case true:
              setConfirmationModalOpen(true);
              break;
            default:
              handleUpgrade();
              break;
            }
          }}
          disabled={isLoading || needsPaymentMethod}
          style={{
            backgroundColor: isLoading || needsPaymentMethod ? "lightgray" : "#1881e3",
            color: "white",
          }}
        >
          {isLoading ? <CircularProgress color="secondary" size={15} /> : "Upgrade to Lead Capture"}
        </button>
        <div
          style={{
            background: "none",
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
            padding: "4px 8px",
            cursor: "pointer",
            color: "black",
          }}
          className="buttonContainerAlt"
          onClick={isLoading || isLoadingPreview ? null : () => doPreview()}
        >
          {isLoadingPreview ? <CircularProgress color="secondary" size={15} /> : "Preview Pricing"}
        </div>
      </div>
      <PricingPreview
        isLoadingPreview={isLoadingPreview}
        isElcUpgrade={isElcUpgrade && shouldProrateLeads}
        previewLabel={selectedPlanInterval?.label || "Yearly"}
      />

      <ConfirmationModal
        open={confirmationModalOpen && !shouldProrateLeads}
        message={`Are you sure you want to upgrade this team to ELC? They will be ${chargeAutomatically ? "charged" : "billed"} the full amount, including seats.\nREASON: ${isPriceMonthly ? "Monthly" : "No active subscription"}`}
        callback={handleUpgrade}
        onClose={() => setConfirmationModalOpen(false)}
      />
    </div>
  );
};

export default LeadCaptureUpgradeSection;
